<template>
  <div>
    <div>
      <!-- Header -->
      <div class="content-header">
        <div
          class="is-flex is-flex-wrap-wrap is-justify-content-space-between is-mobile is-vcentered"
          style="gap: 1rem;"
        >
          <div>
            <h2 class="title is-5">Gestión de Colaboradores</h2>
            <p class="subtitle is-6">
              Administra las personas asignadas a este proceso
            </p>
          </div>
          <div>
            <div class="buttons">
              <b-button
                v-tooltip.top="{
                  content: 'Actualizar el estado de los colaboradores',
                  classes: 'cultura-tooltip',
                }"
                :loading="isRefreshing"
                @click="handleRefresh"
              >
                <b-icon icon="refresh" size="is-small"></b-icon>
                <span>Actualizar</span>
              </b-button>
              <b-button
                v-tooltip.top="{
                  content: 'Descargar colaboradores en Excel',
                  classes: 'cultura-tooltip',
                }"
                @click="isDownloadModalActive = true"
              >
                <b-icon icon="download" size="is-small"></b-icon>
                <span>Descargar</span>
              </b-button>
              <b-button
                v-tooltip.top="{
                  content: isProcessActive
                    ? 'Enviar la encuesta a todos los colaboradores'
                    : 'Solo puedes enviar correos cuando el proceso está en estado Activo',
                  classes: 'cultura-tooltip',
                }"
                :disabled="!isProcessActive"
                @click="handleSendAllEmails"
              >
                <b-icon icon="send" size="is-small"></b-icon>
                <span>Enviar Todos los Correos</span>
              </b-button>
              <b-button
                type="is-dark button-create"
                @click="isAddModalActive = true"
              >
                <b-icon icon="plus" size="is-small"></b-icon>
                <span>Agregar Colaborador</span>
              </b-button>
              <b-button
                :class="isCalculated ? 'button-with-badge' : ''"
                v-tooltip.top-end="{
                  content: isCalculated
                    ? `Encuestas para calcular: ${isCalculated}`
                    : currentProcess.lastResultDate
                    ? `Último calculo el ${this.formatDate(
                        currentProcess.lastResultDate,
                      )}`
                    : 'No hay encuestas para calcular',
                  classes: 'cultura-tooltip',
                }"
                type="is-link"
                @click="handleCalculated"
                :disabled="!isCalculated"
              >
                <b-icon icon="calculator" size="is-small"></b-icon>
              </b-button>
            </div>
          </div>
        </div>
      </div>

      <!-- Filters -->
      <div class="columns is-vcentered filter-section">
        <div class="column search-column">
          <b-field>
            <b-autocomplete
              v-model="searchTerm"
              :data="searchSuggestions"
              field="label"
              placeholder="Buscar colaboradores..."
              icon="magnify"
              clearable
              open-on-focus
              group-field="group"
              group-options="items"
              :max-height="320"
              class="collaborator-search"
              @typing="onQuickFilterChanged"
              @select="onSuggestionSelected"
            >
              <template #default="{ option }">
                <div class="suggestion-row">
                  <span class="suggestion-label">{{ option.label }}</span>
                  <span class="suggestion-tag has-text-grey is-size-7">
                    {{ option.tag }}
                  </span>
                </div>
              </template>
              <template #empty>Sin coincidencias</template>
            </b-autocomplete>
          </b-field>
        </div>

        <div class="column"></div>

        <div class="column is-narrow">
          <b-dropdown
            v-model="statusFilter"
            multiple
            aria-role="list"
            class="status-filter-dropdown"
          >
            <template #trigger="{ active }">
              <b-button
                :icon-right="active ? 'menu-up' : 'menu-down'"
                class="status-filter-trigger"
              >
                <b-icon
                  icon="filter-variant"
                  size="is-small"
                  class="filter-icon"
                ></b-icon>
                <span class="filter-label">Estado:</span>
                <span v-if="statusFilter.length === 0" class="has-text-grey">
                  Todos
                </span>
                <span
                  v-else-if="statusFilter.length === 1"
                  class="tag"
                  :class="statusConfig[statusFilter[0]].class"
                >
                  <i
                    class="icon-badge mdi"
                    :class="`mdi-${statusConfig[statusFilter[0]].icon}`"
                  ></i>
                  {{ statusConfig[statusFilter[0]].label }}
                </span>
                <span v-else class="has-text-weight-medium">
                  {{ statusFilter.length }} estados
                </span>
              </b-button>
            </template>

            <b-dropdown-item
              custom
              aria-role="listitem"
              class="status-all-item"
              :class="{ 'is-active': statusFilter.length === 0 }"
              @click.native="statusFilter = []"
            >
              <span class="has-text-grey">Todos los estados</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-for="(cfg, key) in statusConfig"
              :key="key"
              :value="key"
              aria-role="listitem"
            >
              <span class="tag" :class="cfg.class">
                <i class="icon-badge mdi" :class="`mdi-${cfg.icon}`"></i>
                {{ cfg.label }}
              </span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>

      <!-- AG Grid Table -->
      <div class="box ag-grid-container">
        <ag-grid-vue
          style="width: 100%; height: 470px"
          class="ag-theme-material"
          :gridOptions="gridOptions"
          :columnDefs="columnDefs"
          :rowData="filteredCollaborators"
          :defaultColDef="defaultColDef"
          :domLayout="'normal'"
          :suppressCellFocus="true"
          :suppressRowClickSelection="true"
          :isFullWidthRow="isFullWidthRow"
          :fullWidthCellRenderer="fullWidthCellRenderer"
          @grid-ready="onGridReady"
          @model-updated="onModelUpdated"
        >
        </ag-grid-vue>
        <div class="row-count-footer">
          Mostrando {{ displayedRowCount }} de {{ collaborators.length }}
          {{ collaborators.length === 1 ? "colaborador" : "colaboradores" }}
        </div>
      </div>

      <!-- Summary Stats -->
      <div class="columns is-multiline stats-section">
        <div class="column is-2 is-6-mobile">
          <div class="box stat-card">
            <div class="title is-3">{{ collaborators.length }}</div>
            <div class="is-size-7 has-text-grey">Total Colaboradores</div>
          </div>
        </div>
        <div class="column is-2 is-6-mobile">
          <div class="box stat-card">
            <div class="title is-3 has-text-info">
              {{ sentCount }}
            </div>
            <div class="is-size-7 has-text-grey">Correos Enviados</div>
          </div>
        </div>
        <div class="column is-2 is-6-mobile">
          <div class="box stat-card">
            <div class="title is-3 has-text-success">
              {{ finishedCount }}
            </div>
            <div class="is-size-7 has-text-grey">Finalizados</div>
          </div>
        </div>
        <div class="column is-2 is-6-mobile">
          <div class="box stat-card">
            <div class="title is-3 has-text-link">
              {{ disagreementCount }}
            </div>
            <div class="is-size-7 has-text-grey">Desacuerdo</div>
          </div>
        </div>
        <div class="column is-4 is-6-mobile">
          <div class="box stat-card">
            <div class="title is-3 has-text-success">
              {{ calculatedCount }}
            </div>
            <div class="is-size-7 has-text-grey">Calculados</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Collaborator Modal -->
    <create-collaborator-modal
      v-model="isAddModalActive"
      :editForm="editForm"
      @handleAddCollaborator="handleAddCollaborator"
      @handleUpdateCollaborator="handleUpdateCollaborator"
    />

    <!-- Download Modal -->
    <b-modal v-model="isDownloadModalActive" :width="480" scroll="keep">
      <div class="box download-modal">
        <h2 class="title is-5">Descargar colaboradores</h2>
        <p class="subtitle is-6 has-text-grey">
          Selecciona qué colaboradores quieres exportar a Excel
        </p>
        <div class="download-options">
          <button class="download-option" @click="downloadCollaborators('all')">
            <span class="download-option__icon">
              <b-icon icon="account-group-outline" size="is-medium"></b-icon>
            </span>
            <span class="download-option__text">
              <span class="download-option__title">
                Todos los colaboradores
              </span>
              <span class="download-option__desc">
                Exporta la lista completa ({{ collaborators.length }})
              </span>
            </span>
          </button>
          <button
            class="download-option"
            @click="downloadCollaborators('pending')"
          >
            <span class="download-option__icon">
              <b-icon icon="account-clock-outline" size="is-medium"></b-icon>
            </span>
            <span class="download-option__text">
              <span class="download-option__title">
                Solo los no finalizados
              </span>
              <span class="download-option__desc">
                Quienes aún no completan la encuesta ({{ pendingCount }})
              </span>
            </span>
          </button>
        </div>
      </div>
    </b-modal>

    <!-- Send Failures Modal -->
    <b-modal v-model="isFailuresModalActive" :width="580" scroll="keep">
      <div class="box failures-modal">
        <h2 class="title is-5">Correos no enviados</h2>
        <p class="subtitle is-6 has-text-grey">{{ sendFailures.message }}</p>

        <div class="failures-summary">
          <span class="tag is-success is-light">
            <i class="icon-badge mdi mdi-check-circle"></i>
            {{ sendFailures.sent }} enviados
          </span>
          <span class="tag is-danger is-light">
            <i class="icon-badge mdi mdi-alert-circle"></i>
            {{ sendFailures.failed }} fallidos
          </span>
        </div>

        <div class="failures-list">
          <div
            v-for="(failure, index) in sendFailures.items"
            :key="index"
            class="failures-row"
          >
            <div class="failures-row__info">
              <span class="failures-row__name">
                {{
                  failure.name && failure.name.length
                    ? failure.name
                    : "Sin nombre"
                }}
              </span>
              <span class="failures-row__email">
                {{
                  failure.email && failure.email.length
                    ? failure.email
                    : "Sin correo"
                }}
              </span>
            </div>
            <span class="failures-row__reason">{{ failure.reason }}</span>
          </div>
        </div>

        <div class="failures-modal__actions">
          <b-button @click="isFailuresModalActive = false">Cerrar</b-button>
          <b-button type="is-link" @click="downloadFailures">
            <b-icon icon="download" size="is-small"></b-icon>
            <span>Exportar a Excel</span>
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- Temporary results -->
    <b-modal
      v-model="showModalResults"
      :width="940"
      scroll="keep"
      @close="closeResults"
    >
      <div class="box is-flex" style="gap: 0.5rem">
        <div>
          <h2 class="title is-5">Actual</h2>
          <p class="subtitle is-6">
            Todas las variables calculadas para "hoy"
          </p>
          <br />
          <div>
            <ul v-if="results && results.today">
              <li class="is-flex" style="gap: 0.5rem">
                <strong>Media-Clan:</strong>
                <span>{{ results.today.ClanM_hoy || "-" }}</span>
              </li>
              <li class="is-flex" style="gap: 0.5rem">
                <strong>Media-Adhocracia:</strong>
                <span>{{ results.today.AdhocraciaM_hoy || "-" }}</span>
              </li>

              <li class="is-flex" style="gap: 0.5rem">
                <strong>Media-Jerarquica:</strong>
                <span>{{ results.today.JerarquicaM_hoy || "-" }}</span>
              </li>

              <li class="is-flex" style="gap: 0.5rem">
                <strong>Media-Mercado:</strong>
                <span>{{ results.today.MercadoM_hoy || "-" }}</span>
              </li>
              <li class="is-flex" style="gap: 0.5rem">
                <strong>Media-Suma:</strong>
                <span>{{ results.today.Sumam_hoy || "-" }}</span>
              </li>
              <hr />
              <li class="is-flex" style="gap: 0.5rem">
                <strong>Geometrica-Clan:</strong>
                <span>{{ results.today.Clang_hoy || "-" }}</span>
              </li>
              <li class="is-flex" style="gap: 0.5rem">
                <strong>Geometrica-Adhocracia:</strong>
                <span>{{ results.today.Adhocraciag_hoy || "-" }}</span>
              </li>

              <li class="is-flex" style="gap: 0.5rem">
                <strong>Geometrica-Jerarquica:</strong>
                <span>{{ results.today.Jerarquicag_hoy || "-" }}</span>
              </li>

              <li class="is-flex" style="gap: 0.5rem">
                <strong>Geometrica-Mercado:</strong>
                <span>{{ results.today.Mercadog_hoy || "-" }}</span>
              </li>
              <li class="is-flex" style="gap: 0.5rem">
                <strong>Geometrica-Suma:</strong>
                <span>{{ results.today.Sumag_hoy || "-" }}</span>
              </li>
              <hr />
              <li class="is-flex" style="gap: 0.5rem">
                <strong>Constante:</strong>
                <span>{{ results.today.constant_hoy || "-" }}</span>
              </li>
              <li class="is-flex" style="gap: 0.5rem">
                <strong>Clan:</strong>
                <span>{{ results.today.Clan_hoy || "-" }}</span>
              </li>
              <li class="is-flex" style="gap: 0.5rem">
                <strong>Adhocracia:</strong>
                <span>{{ results.today.Adhocracia_hoy || "-" }}</span>
              </li>
              <li class="is-flex" style="gap: 0.5rem">
                <strong>Jerarquica:</strong>
                <span>{{ results.today.Jerarquica_hoy || "-" }}</span>
              </li>
              <li class="is-flex" style="gap: 0.5rem">
                <strong>Mercado:</strong>
                <span>{{ results.today.Mercado_hoy || "-" }}</span>
              </li>
              <li class="is-flex" style="gap: 0.5rem">
                <strong>Suma:</strong>
                <span>{{ results.today.Suma_hoy || "-" }}</span>
              </li>
              <hr />
              <li class="has-text-centered">
                Identificación de cuadrantes
              </li>
              <hr />
              <li class="is-flex" style="gap: 0.5rem">
                <strong>Cultura Actual #1:</strong>
                <span>{{ results.today.maxValue || "-" }}</span>
                <b-tag>{{ results.today.maxKey || "-" }}</b-tag>
              </li>
              <li
                v-if="results.today.tieCount > 1"
                class="is-flex"
                style="gap: 0.5rem"
              >
                <strong>Cultura Actual #2:</strong>
                <span>{{ results.today.secondMaxValue || "-" }}</span>
                <b-tag>{{ results.today.secondMaxKey || "-" }}</b-tag>
              </li>
              <hr />
              <li class="is-flex" style="gap: 0.5rem">
                <strong>CE #1:</strong>
                <span>{{ results.today.CE.maxValue || "-" }}</span>
                <b-tag>{{ results.today.CE.maxKey || "-" }}</b-tag>
              </li>
              <li
                v-if="results.today.CE.tieCount > 1"
                class="is-flex"
                style="gap: 0.5rem"
              >
                <strong>CE #2:</strong>
                <span>{{ results.today.CE.secondMaxValue || "-" }}</span>
                <b-tag>{{ results.today.CE.secondMaxKey || "-" }}</b-tag>
              </li>
              <hr />
              <li class="is-flex" style="gap: 0.5rem">
                <strong>CD #1:</strong>
                <span>{{ results.today.CD.maxValue || "-" }}</span>
                <b-tag>{{ results.today.CD.maxKey || "-" }}</b-tag>
              </li>
              <li
                v-if="results.today.CD.tieCount > 1"
                class="is-flex"
                style="gap: 0.5rem"
              >
                <strong>CD #2:</strong>
                <span>{{ results.today.CD.secondMaxValue || "-" }}</span>
                <b-tag>{{ results.today.CD.secondMaxKey || "-" }}</b-tag>
              </li>
              <hr />
              <li class="is-flex" style="gap: 0.5rem">
                <strong>VL #1:</strong>
                <span>{{ results.today.VL.maxValue || "-" }}</span>
                <b-tag>{{ results.today.VL.maxKey || "-" }}</b-tag>
              </li>
              <li
                v-if="results.today.VL.tieCount > 1"
                class="is-flex"
                style="gap: 0.5rem"
              >
                <strong>VL #2:</strong>
                <span>{{ results.today.VL.secondMaxValue || "-" }}</span>
                <b-tag>{{ results.today.VL.secondMaxKey || "-" }}</b-tag>
              </li>
              <hr />
              <li class="is-flex" style="gap: 0.5rem">
                <strong>LO #1:</strong>
                <span>{{ results.today.LO.maxValue || "-" }}</span>
                <b-tag>{{ results.today.LO.maxKey || "-" }}</b-tag>
              </li>
              <li
                v-if="results.today.LO.tieCount > 1"
                class="is-flex"
                style="gap: 0.5rem"
              >
                <strong>LO #2:</strong>
                <span>{{ results.today.LO.secondMaxValue || "-" }}</span>
                <b-tag>{{ results.today.LO.secondMaxKey || "-" }}</b-tag>
              </li>
              <hr />
              <li class="is-flex" style="gap: 0.5rem">
                <strong>EE #1:</strong>
                <span>{{ results.today.EE.maxValue || "-" }}</span>
                <b-tag>{{ results.today.EE.maxKey || "-" }}</b-tag>
              </li>
              <li
                v-if="results.today.EE.tieCount > 1"
                class="is-flex"
                style="gap: 0.5rem"
              >
                <strong>EE #2:</strong>
                <span>{{ results.today.EE.secondMaxValue || "-" }}</span>
                <b-tag>{{ results.today.EE.secondMaxKey || "-" }}</b-tag>
              </li>
              <hr />
              <li class="is-flex" style="gap: 0.5rem">
                <strong>SP #1:</strong>
                <span>{{ results.today.SP.maxValue || "-" }}</span>
                <b-tag>{{ results.today.SP.maxKey || "-" }}</b-tag>
              </li>
              <li
                v-if="results.today.SP.tieCount > 1"
                class="is-flex"
                style="gap: 0.5rem"
              >
                <strong>SP #2:</strong>
                <span>{{ results.today.SP.secondMaxValue || "-" }}</span>
                <b-tag>{{ results.today.SP.secondMaxKey || "-" }}</b-tag>
              </li>
              <hr />
              <li class="is-flex" style="gap: 0.5rem">
                <strong>Dimensión-CE:</strong>
                <span>{{ results.today.CEdim.maxKey || "-" }}</span>
              </li>
              <li class="is-flex" style="gap: 0.5rem">
                <strong>Dimensió-CD:</strong>
                <span>{{ results.today.CDdim.maxKey || "-" }}</span>
              </li>
              <hr />
              <li class="is-flex" style="gap: 0.5rem">
                <strong>Cultura:</strong>
                <span>{{ results.today.cultura || "-" }}</span>
              </li>
            </ul>
          </div>
        </div>
        <div>
          <h2 class="title is-5">Deseada</h2>
          <p class="subtitle is-6">
            Todas las variables calculadas para "futuro"
          </p>
          <br />
          <div>
            <ul v-if="results && results.future">
              <li class="is-flex" style="gap: 0.5rem">
                <strong>Media-Clan:</strong>
                <span>{{ results.future.ClanM_futuro || "-" }}</span>
              </li>
              <li class="is-flex" style="gap: 0.5rem">
                <strong>Media-Adhocracia:</strong>
                <span>{{ results.future.AdhocraciaM_futuro || "-" }}</span>
              </li>

              <li class="is-flex" style="gap: 0.5rem">
                <strong>Media-Jerarquica:</strong>
                <span>{{ results.future.JerarquicaM_futuro || "-" }}</span>
              </li>

              <li class="is-flex" style="gap: 0.5rem">
                <strong>Media-Mercado:</strong>
                <span>{{ results.future.MercadoM_futuro || "-" }}</span>
              </li>
              <li class="is-flex" style="gap: 0.5rem">
                <strong>Media-Suma:</strong>
                <span>{{ results.future.Sumam_futuro || "-" }}</span>
              </li>
              <hr />
              <li class="is-flex" style="gap: 0.5rem">
                <strong>Geometrica-Clan:</strong>
                <span>{{ results.future.Clang_futuro || "-" }}</span>
              </li>
              <li class="is-flex" style="gap: 0.5rem">
                <strong>Geometrica-Adhocracia:</strong>
                <span>{{ results.future.Adhocraciag_futuro || "-" }}</span>
              </li>

              <li class="is-flex" style="gap: 0.5rem">
                <strong>Geometrica-Jerarquica:</strong>
                <span>{{ results.future.Jerarquicag_futuro || "-" }}</span>
              </li>

              <li class="is-flex" style="gap: 0.5rem">
                <strong>Geometrica-Mercado:</strong>
                <span>{{ results.future.Mercadog_futuro || "-" }}</span>
              </li>
              <li class="is-flex" style="gap: 0.5rem">
                <strong>Geometrica-Suma:</strong>
                <span>{{ results.future.Sumag_hoy || "-" }}</span>
              </li>
              <hr />
              <li class="is-flex" style="gap: 0.5rem">
                <strong>Constante:</strong>
                <span>{{ results.future.constant_futuro || "-" }}</span>
              </li>
              <li class="is-flex" style="gap: 0.5rem">
                <strong>Clan:</strong>
                <span>{{ results.today.Clan_futuro || "-" }}</span>
              </li>
              <li class="is-flex" style="gap: 0.5rem">
                <strong>Adhocracia:</strong>
                <span>{{ results.future.Adhocracia_futuro || "-" }}</span>
              </li>
              <li class="is-flex" style="gap: 0.5rem">
                <strong>Jerarquica:</strong>
                <span>{{ results.future.Jerarquica_futuro || "-" }}</span>
              </li>
              <li class="is-flex" style="gap: 0.5rem">
                <strong>Mercado:</strong>
                <span>{{ results.future.Mercado_futuro || "-" }}</span>
              </li>
              <li class="is-flex" style="gap: 0.5rem">
                <strong>Suma:</strong>
                <span>{{ results.future.Suma_futuro || "-" }}</span>
              </li>
              <hr />
              <li class="has-text-centered">
                Identificación de cuadrantes
              </li>
              <hr />
              <li class="is-flex" style="gap: 0.5rem">
                <strong>Cultura Deseada #1:</strong>
                <span>{{ results.future.maxValue || "-" }}</span>
                <b-tag>{{ results.future.maxKey || "-" }}</b-tag>
              </li>
              <li
                v-if="results.future.tieCount > 1"
                class="is-flex"
                style="gap: 0.5rem"
              >
                <strong>Cultura Deseada #2:</strong>
                <span>{{ results.future.secondMaxValue || "-" }}</span>
                <b-tag>{{ results.future.secondMaxKey || "-" }}</b-tag>
              </li>
              <hr />
              <li class="is-flex" style="gap: 0.5rem">
                <strong>CE #1:</strong>
                <span>{{ results.future.CE.maxValue || "-" }}</span>
                <b-tag>{{ results.future.CE.maxKey || "-" }}</b-tag>
              </li>
              <li
                v-if="results.future.CE.tieCount > 1"
                class="is-flex"
                style="gap: 0.5rem"
              >
                <strong>CE #2:</strong>
                <span>{{ results.future.CE.secondMaxValue || "-" }}</span>
                <b-tag>{{ results.future.CE.secondMaxKey || "-" }}</b-tag>
              </li>
              <hr />
              <li class="is-flex" style="gap: 0.5rem">
                <strong>CD #1:</strong>
                <span>{{ results.future.CD.maxValue || "-" }}</span>
                <b-tag>{{ results.future.CD.maxKey || "-" }}</b-tag>
              </li>
              <li
                v-if="results.future.CD.tieCount > 1"
                class="is-flex"
                style="gap: 0.5rem"
              >
                <strong>CD #2:</strong>
                <span>{{ results.future.CD.secondMaxValue || "-" }}</span>
                <b-tag>{{ results.future.CD.secondMaxKey || "-" }}</b-tag>
              </li>
              <hr />
              <li class="is-flex" style="gap: 0.5rem">
                <strong>VL #1:</strong>
                <span>{{ results.future.VL.maxValue || "-" }}</span>
                <b-tag>{{ results.future.VL.maxKey || "-" }}</b-tag>
              </li>
              <li
                v-if="results.future.VL.tieCount > 1"
                class="is-flex"
                style="gap: 0.5rem"
              >
                <strong>VL #2:</strong>
                <span>{{ results.future.VL.secondMaxValue || "-" }}</span>
                <b-tag>{{ results.future.VL.secondMaxKey || "-" }}</b-tag>
              </li>
              <hr />
              <li class="is-flex" style="gap: 0.5rem">
                <strong>LO #1:</strong>
                <span>{{ results.future.LO.maxValue || "-" }}</span>
                <b-tag>{{ results.future.LO.maxKey || "-" }}</b-tag>
              </li>
              <li
                v-if="results.future.LO.tieCount > 1"
                class="is-flex"
                style="gap: 0.5rem"
              >
                <strong>LO #2:</strong>
                <span>{{ results.future.LO.secondMaxValue || "-" }}</span>
                <b-tag>{{ results.future.LO.secondMaxKey || "-" }}</b-tag>
              </li>
              <hr />
              <li class="is-flex" style="gap: 0.5rem">
                <strong>EE #1:</strong>
                <span>{{ results.future.EE.maxValue || "-" }}</span>
                <b-tag>{{ results.future.EE.maxKey || "-" }}</b-tag>
              </li>
              <li
                v-if="results.future.EE.tieCount > 1"
                class="is-flex"
                style="gap: 0.5rem"
              >
                <strong>EE #2:</strong>
                <span>{{ results.future.EE.secondMaxValue || "-" }}</span>
                <b-tag>{{ results.future.EE.secondMaxKey || "-" }}</b-tag>
              </li>
              <hr />
              <li class="is-flex" style="gap: 0.5rem">
                <strong>SP #1:</strong>
                <span>{{ results.future.SP.maxValue || "-" }}</span>
                <b-tag>{{ results.future.SP.maxKey || "-" }}</b-tag>
              </li>
              <li
                v-if="results.future.SP.tieCount > 1"
                class="is-flex"
                style="gap: 0.5rem"
              >
                <strong>SP #2:</strong>
                <span>{{ results.future.SP.secondMaxValue || "-" }}</span>
                <b-tag>{{ results.future.SP.secondMaxKey || "-" }}</b-tag>
              </li>
              <hr />
              <li class="is-flex" style="gap: 0.5rem">
                <strong>Dimensión-CE:</strong>
                <span>{{ results.future.CEdim.maxKey || "-" }}</span>
              </li>
              <li class="is-flex" style="gap: 0.5rem">
                <strong>Dimensió-CD:</strong>
                <span>{{ results.future.CDdim.maxKey || "-" }}</span>
              </li>
              <hr />
              <li class="is-flex" style="gap: 0.5rem">
                <strong>Cultura:</strong>
                <span>{{ results.future.cultura || "-" }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- Email History -->
    <email-history-modal
      v-model="emailHistoryModal.isOpen"
      :data="emailHistoryModal.data"
    />
  </div>
</template>

<script>
import { mapState } from "vuex";
import * as XLSX from "xlsx";

// Components
import { AgGridVue } from "ag-grid-vue";
import ActionsCollaborator from "../../components/Collaborator/ActionsCollaborator.vue";
import CreateCollaboratorModal from "../../components/Collaborator/CreateCollaboratorModal.vue";
import EmailHistoryModal from "../../components/Collaborator/EmailHistoryModal.vue";

// Store
import store from "../../store";
import {
  fetchCollaborators,
  createCollaborator,
  sendSurveyCollaborator,
  deleteCollaborator,
  calculatedSurveysCollaborator,
  fetchHistoryEmail,
  sendSurveys,
  updateCollaborator,
  fetchProcessById,
  calculateBoardByProcess,
  fetchDependencies,
} from "../../api";
import { formatDate } from "../../helpers";

export default {
  name: "CollaboratorManagement",
  components: {
    AgGridVue,
    EmailHistoryModal,
    CreateCollaboratorModal,
    /* eslint-disable-next-line vue/no-unused-components */
    actionsCollaborator: ActionsCollaborator,
  },
  data() {
    return {
      results: null, // TEMPORAL
      showModalResults: false, // TEMPORAL
      isRefreshing: false,
      // Modals
      isAddModalActive: false,
      isDownloadModalActive: false,
      isFailuresModalActive: false,
      sendFailures: {
        sent: 0,
        failed: 0,
        message: "",
        items: [],
      },
      editForm: {},
      emailHistoryModal: {
        isOpen: false,
        data: {},
      },
      // Dependencies (for dynamic columns)
      dependencies: [],
      // Filter
      searchTerm: "",
      statusFilter: [],
      statusConfig: {
        created: {
          label: "Creada",
          class: "is-primary is-light",
          icon: "moon-new",
        },
        sent: {
          label: "Enviada",
          class: "is-info is-light",
          icon: "send",
        },
        started: {
          label: "Iniciada",
          class: "is-warning is-light",
          icon: "play-circle",
        },
        finished: {
          label: "Finalizada",
          class: "is-success is-light",
          icon: "check-circle",
        },
        calculated: {
          label: "Calculada",
          class: "is-success is-light",
          icon: "calculator",
        },
        expired: {
          label: "Expirada",
          class: "is-danger is-light",
          icon: "clock-alert",
        },
        disagreement: {
          label: "Desacuerdo",
          class: "is-warning is-light",
          icon: "alert-circle",
        },
      },
      // Mailjet delivery status (kept up to date by the shared Mailjet webhook).
      mailjetStatusConfig: {
        none: {
          label: "Sin estado",
          class: "is-light",
          tooltip: "Aún no se ha enviado el correo.",
        },
        success: {
          label: "Aceptado",
          class: "is-success is-light",
          tooltip:
            "El servidor destino aceptó el email. La entrega final depende del proveedor del destinatario.",
        },
        sent: {
          label: "Entregado",
          class: "is-success is-light",
          tooltip:
            "El servidor SMTP destino aceptó el mensaje. No garantiza que llegó a la bandeja.",
        },
        open: {
          label: "Abierto",
          class: "is-link is-light",
          tooltip: "El destinatario abrió el email.",
        },
        click: {
          label: "Clic",
          class: "is-info is-light",
          tooltip: "El destinatario hizo clic en al menos un enlace del email.",
        },
        bounce_hard: {
          label: "Rebote definitivo",
          class: "is-danger is-light",
          tooltip:
            "Dirección inválida o servidor inexistente. Se bloquea automáticamente.",
        },
        bounce_soft: {
          label: "Rebote temporal",
          class: "is-warning is-light",
          tooltip:
            "Buzón lleno o problema de conexión. Se reintentará el envío; tras 5 días se convierte en definitivo.",
        },
        spam: {
          label: "Spam",
          class: "is-dark",
          tooltip: "El destinatario marcó el email como spam.",
        },
        blocked: {
          label: "Bloqueado",
          class: "is-light",
          tooltip:
            "Bloqueado proactivamente por rebotes previos, spam u otras razones para proteger tu reputación.",
        },
      },
      // Table
      gridApi: null,
      gridColumnApi: null,
      displayedRowCount: 0,
      isFullWidthRow: null,
      fullWidthCellRenderer: null,
      gridOptions: {
        context: {
          componentParent: this,
        },
        overlayNoRowsTemplate: `
          <div class="custom-no-rows">
            <p style="color: #7a7a7a">Aún no has creado ningún colaborador</p>
          </div>
        `,
      },
      defaultColDef: {
        sortable: true,
        filter: true,
        autoHeight: true,
        resizable: false,
        suppressMovable: true,
        suppressMenu: true,
      },
    };
  },
  computed: {
    ...mapState(["isMovil", "idtoken"]),
    currentProcess() {
      return store.state.selectedProcess;
    },
    isCalculated() {
      return this.collaborators.filter((c) => c.status === "finished").length;
    },
    isProcessActive() {
      return !!this.currentProcess && this.currentProcess.status === "active";
    },
    // KPIS
    collaborators() {
      return store.state.collaborators;
    },
    filteredCollaborators() {
      if (this.statusFilter.length === 0) return this.collaborators;
      return this.collaborators.filter((c) =>
        this.statusFilter.includes(c.status),
      );
    },
    searchSuggestions() {
      const term = (this.searchTerm || "").trim().toLowerCase();
      if (!term) return [];

      const matches = (val) =>
        val != null &&
        String(val)
          .toLowerCase()
          .includes(term);

      const namesSet = new Set();
      const emailsSet = new Set();
      const docsSet = new Set();
      const depValuesByDep = {};

      const depNameById = {};
      this.dependencies.forEach((d) => {
        depNameById[String(d._id)] = d.name;
      });

      this.collaborators.forEach((c) => {
        if (matches(c.name)) namesSet.add(c.name);
        if (matches(c.email)) emailsSet.add(c.email);
        if (matches(c.identification)) docsSet.add(c.identification);

        (c.dependencies || []).forEach((d) => {
          const id =
            d.dependency && d.dependency._id ? d.dependency._id : d.dependency;
          const depName = depNameById[String(id)];
          if (!depName) return;
          if (!matches(d.value)) return;
          if (!depValuesByDep[depName]) depValuesByDep[depName] = new Set();
          depValuesByDep[depName].add(d.value);
        });
      });

      const toItems = (set, tag) =>
        Array.from(set)
          .slice(0, 5)
          .map((label) => ({ label, tag }));

      const groups = [];
      if (namesSet.size) {
        groups.push({
          group: "Colaboradores",
          items: toItems(namesSet, "Colaborador"),
        });
      }
      if (emailsSet.size) {
        groups.push({ group: "Correos", items: toItems(emailsSet, "Correo") });
      }
      if (docsSet.size) {
        groups.push({
          group: "Documentos",
          items: toItems(docsSet, "Documento"),
        });
      }
      Object.entries(depValuesByDep).forEach(([depName, set]) => {
        groups.push({ group: depName, items: toItems(set, depName) });
      });

      return groups;
    },
    dependencyColumns() {
      return this.dependencies.map((dep) => ({
        headerName: dep.name,
        colId: `dep-${dep._id}`,
        flex: 1,
        valueGetter: (params) => {
          const found = (params.data.dependencies || []).find((d) => {
            const id =
              d.dependency && d.dependency._id
                ? d.dependency._id
                : d.dependency;
            return String(id) === String(dep._id);
          });
          return found && found.value != null ? found.value : "";
        },
        getQuickFilterText: (params) => String(params.value || ""),
        cellRenderer: (params) => {
          if (!params.value) {
            return `<span class="has-text-grey-light is-size-7">—</span>`;
          }
          return `<span class="tag is-light">${params.value}</span>`;
        },
        cellStyle: {
          display: "flex",
          alignItems: "center",
        },
      }));
    },
    columnDefs() {
      return [
        {
          headerName: "",
          field: "typeSurvey",
          width: 80,
          resizable: false,
          pinned: "left",
          lockPinned: true,
          cellRenderer: (params) => {
            const { typeSurvey: type } = params.data;
            const icon = type ? "account-star-outline" : "account-outline";
            const classCustom = type
              ? "is-success is-light"
              : "is-info is-light";

            return `
              <span class="tag ${classCustom}">
                <i class="mdi mdi-${icon}" style="font-size: 18px"></i>
              </span>
            `;
          },
          cellStyle: {
            display: "flex",
          },
        },
        {
          headerName: "Colaborador",
          field: "name",
          width: 260,
          pinned: "left",
          lockPinned: true,
          cellRenderer: (params) => {
            return `
              <div style="width: 100%; line-height: 1.5;">
                 <div class="mt-1"">${params.data.name}</div>
                <div class="is-size-7 has-text-grey mb-1">
                    <i class="mdi mdi-email" style="font-size: 14px;"></i>
                    ${
                      params.data.email.length
                        ? params.data.email
                        : "Sin correo"
                    }
                </div>
            </div>
            `;
          },
        },
        {
          headerName: "Documento",
          field: "identification",
          flex: 1,
          cellStyle: {
            display: "flex",
            alignItems: "center",
            fontSize: "0.813rem",
            color: "#7a7a7a",
          },
        },
        ...this.dependencyColumns,
        {
          headerName: "Estado",
          field: "status",
          width: 160,
          pinned: "right",
          lockPinned: true,
          cellClass: "badge-cell",
          cellRenderer: (params) => {
            return this.createStatusBadge(params.data.status);
          },
          cellStyle: {
            display: "flex",
            alignItems: "center",
          },
        },
        {
          headerName: "Correo",
          field: "mailRecord.status",
          width: 160,
          pinned: "right",
          lockPinned: true,
          cellClass: "badge-cell",
          cellRenderer: (params) => {
            return this.createMailjetBadge(
              params.data.mailRecord && params.data.mailRecord.status,
            );
          },
          cellStyle: {
            display: "flex",
            alignItems: "center",
          },
        },
        {
          headerName: "Fecha Ingreso",
          field: "createdAt",
          flex: 1,
          valueFormatter: (params) => {
            return formatDate(params.value);
          },
          cellStyle: {
            display: "flex",
            alignItems: "center",
            fontSize: "0.813rem",
            color: "#7a7a7a",
          },
        },
        {
          headerName: "Acciones",
          field: "id",
          width: 130,
          pinned: "right",
          lockPinned: true,
          cellRenderer: "actionsCollaborator",
          cellStyle: {
            display: "flex",
            alignItems: "center",
          },
          onCellClicked: (params) => {
            params.api.deselectAll();
          },
          suppressNavigable: true,
          sortable: false,
        },
      ];
    },
    calculatedCount() {
      return this.collaborators.filter((c) => c.status === "calculated").length;
    },
    finishedCount() {
      return this.collaborators.filter((c) =>
        ["finished", "calculated"].includes(c.status),
      ).length;
    },
    pendingCount() {
      return this.collaborators.filter(
        (c) => !["finished", "calculated"].includes(c.status),
      ).length;
    },
    sentCount() {
      return this.collaborators.filter((c) => c.status !== "sent").length;
    },
    disagreementCount() {
      return this.collaborators.filter((c) => c.status === "disagreement")
        .length;
    },
    // Validate Form
    nameError() {
      if (!this.touched.name) return null;
      if (!this.form.name) return "Campo obligatorio";
      return null;
    },
    emailError() {
      if (!this.touched.email) return null;
      if (!this.form.email) return "Campo obligatorio";

      const input = this.$refs.emailInput.$refs.input || this.$refs.emailInput;
      if (input && !input.checkValidity()) {
        return "Correo electronico inválido";
      }

      return null;
    },
    identificationError() {
      if (!this.touched.identification) return null;
      if (!this.form.identification) return "Campo obligatorio";
      return null;
    },
    isFormValid() {
      const noErrors =
        !this.nameError && !this.emailError & !this.identificationError;
      const allFilled =
        this.form.name.trim() !== "" &&
        this.form.email.trim() !== "" &&
        this.form.identification.trim() !== "";
      return noErrors && allFilled;
    },
  },
  created() {
    this.isFullWidthRow = this.isFullWidthRowFunc.bind(this);
    this.fullWidthCellRenderer = this.fullWidthCellRendererFunc.bind(this);

    if (this.idtoken) {
      this.getCollaborators();
      this.getDependencies();
    }
  },
  methods: {
    // API
    async getCollaborators() {
      await fetchCollaborators(this.currentProcess._id)
        .then((res) => store.dispatch("setCollaborators", res))
        .then(() => {
          fetchProcessById(this.currentProcess._id)
            .then((res) => store.dispatch("selectProcess", res))
            .catch((err) => console.log(err));
        })
        .catch((err) => {
          console.log(err);
          this.$store.dispatch("notify", {
            message: "Error al obtner los colaboradores",
            type: "is-danger",
          });
        });
    },
    async handleRefresh() {
      this.isRefreshing = true;
      await this.getCollaborators();
      this.isRefreshing = false;
      this.$buefy.toast.open({
        message: "Estado de los colaboradores actualizado",
        type: "is-success",
      });
    },
    async getDependencies() {
      await fetchDependencies(this.currentProcess._id)
        .then((res) => (this.dependencies = res || []))
        .catch((err) => console.log(err));
    },
    // Table
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
    },
    onModelUpdated() {
      if (this.gridApi) {
        this.displayedRowCount = this.gridApi.getDisplayedRowCount();
      }
    },
    isFullWidthRowFunc(params) {
      return false;
    },
    fullWidthCellRendererFunc(params) {
      const collaborator = params.data;
      const statusBadge = this.getStatusLabel(collaborator.status);
      const statusClass = this.getStatusTagClass(collaborator.status);

      return `
        <div class="collaborator-card-mobile" data-id="${collaborator.id}">
          <!-- Card Header -->
          <div class="card-header-mobile">
            <div class="collaborator-info-mobile">
              <figure style="margin: 0; width: 48px; height: 48px; flex-shrink: 0;">
                <img
                  src="${collaborator.avatar || "/placeholder.svg"}"
                  alt="${collaborator.name}"
                  style="width: 100%; height: 100%; border-radius: 50%; object-fit: cover;"
                />
              </figure>
              <div class="collaborator-details-mobile">
                <div style="font-weight: 500;">${collaborator.name}</div>
                <div style="font-size: 0.875rem; color: #7a7a7a; display: flex; align-items: center; gap: 0.25rem; margin-top: 0.25rem;">
                  <span class="icon is-small">
                    <i class="mdi mdi-email"></i>
                  </span>
                  <span style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${
                    collaborator.email
                  }</span>
                </div>
                <div style="font-size: 0.875rem; color: #7a7a7a; margin-top: 0.25rem;">
                  ${collaborator.department}
                </div>
              </div>
            </div>
            <div class="card-actions-mobile">
              <span class="tag ${statusClass}">${statusBadge}</span>
              <div style="display: flex; gap: 0.25rem; margin-top: 0.5rem;">
                <button class="button is-small send-email-button-mobile" data-id="${
                  collaborator.id
                }" title="Enviar correo">
                  <span class="icon is-small">
                    <i class="mdi mdi-email"></i>
                  </span>
                </button>
                <button class="button is-small action-button-mobile" data-id="${
                  collaborator.id
                }">
                  <span class="icon is-small">
                    <i class="mdi mdi-dots-horizontal"></i>
                  </span>
                </button>
              </div>
            </div>
          </div>

          <!-- Card Footer -->
          <div class="card-footer-mobile">
            <span style="font-size: 0.875rem; color: #7a7a7a;">
              Ingresó: ${formatDate(collaborator.joinDate)}
            </span>
          </div>
        </div>
      `;
    },
    onQuickFilterChanged() {
      if (this.gridApi) {
        this.gridApi.setQuickFilter(this.searchTerm);
      }
    },
    onSuggestionSelected(option) {
      if (!option) return;
      this.searchTerm = option.label;
      this.onQuickFilterChanged();
    },
    createStatusBadge(status) {
      const config = this.statusConfig[status] || this.statusConfig.created;
      return `<span class="tag tag-icon ${config.class}"><i class="icon-badge mdi mdi-${config.icon}"></i>${config.label}</span>`;
    },
    createMailjetBadge(status) {
      if (!status || !status.length) {
        const cfg = this.mailjetStatusConfig.none;
        return `<span class="tag ${cfg.class}" title="${cfg.tooltip}">${cfg.label}</span>`;
      }
      // Bounce events arrive as "bounce hard" / "bounce soft" (with a space).
      const value = status.toLowerCase();
      const key = value.startsWith("bounce") ? value.replace(" ", "_") : value;
      const cfg = this.mailjetStatusConfig[key];
      if (!cfg) {
        return `<span class="tag is-light">${status}</span>`;
      }
      return `<span class="tag ${cfg.class}" title="${cfg.tooltip}">${cfg.label}</span>`;
    },
    getStatusLabel(status) {
      const labels = {
        created: "Creado",
        sent: "Enviado",
        started: "Iniciado",
        finished: "Finalizado",
        calculated: "Calculado",
        expired: "Expirado",
        disagreement: "Desacuerdo",
      };
      return labels[status] || "Creado";
    },
    getStatusTagClass(status) {
      const classes = {
        created: "is-light",
        sent: "is-info is-light",
        started: "is-warning is-light",
        finished: "is-success is-light",
        calculated: "is-link is-light",
        expired: "is-danger is-light",
        disagreement: "is-warning is-light",
      };
      return classes[status] || "is-light";
    },
    // Actions
    async handleAddCollaborator(form) {
      await createCollaborator(form)
        .then(async () => {
          await this.getCollaborators().finally(() => {
            this.isAddModalActive = false;
            this.$store.dispatch("notify", {
              message: "Colaborador creado correctamente.",
              type: "is-success",
            });
          });
        })
        .catch((err) => {
          console.log(err);
          this.$store.dispatch("notify", {
            message: "Error al crear el colaborador",
            type: "is-danger",
          });
        });
    },
    async handleUpdateCollaborator(form) {
      await updateCollaborator({
        _id: form._id,
        name: form.name,
        email: form.email,
        identification: form.identification,
        dependencies: form.dependencies,
      })
        .then(async () => {
          await this.getCollaborators().finally(() => {
            this.isAddModalActive = false;
            this.$store.dispatch("notify", {
              message: "Colaborador actualizado correctamente.",
              type: "is-success",
            });
          });
        })
        .catch((err) => {
          console.log(err);
          this.$store.dispatch("notify", {
            message: "Error al actualizar los colaboradores",
            type: "is-danger",
          });
        });
    },
    handleShowEdit(collaborator) {
      this.editForm = collaborator;
      this.isAddModalActive = true;
    },
    async handleDeleteCollaborator(id) {
      const config = {
        title: "¿Estás seguro de eliminarl@?",
        text: "Se eliminará toda la información relacionada al colaborador.",
        icon: "warning",
        showCancelButton: true,
        cancelButtonColor: "#de5b5b",
        confirmButtonText: "Sí, eliminar",
      };
      await this.$swal(config).then(async (result) => {
        if (result.isConfirmed) {
          await deleteCollaborator(id)
            .then(async (res) => {
              await this.getCollaborators().finally(() => {
                this.$store.dispatch("notify", {
                  message: "Colaborador eliminado correctamente.",
                  type: "is-success",
                });
              });
            })
            .catch((err) => {
              console.log(err);
              this.$store.dispatch("notify", {
                message: "Error al eliminar el colaborador.",
                type: "is-danger",
              });
            });
        }
      });
    },
    // Maps a failed email request to a clear, specific reason for the user.
    resolveEmailErrorMessage(err, fallback) {
      const backendMessage =
        err && err.response && err.response.data
          ? err.response.data.message
          : null;

      const reasons = {
        "Collaborator does not exist":
          "El colaborador no existe o su encuesta ya fue finalizada.",
        "There are no contributors":
          "No hay colaboradores con un correo válido para enviarles la encuesta.",
        "Error saving mail log":
          "El correo se envió, pero no se pudo registrar el envío. Revisa el historial de correos.",
        "Error sending survey":
          "El proveedor de correo rechazó el envío. Verifica que el correo del colaborador sea válido.",
        "Error sending surveys":
          "El proveedor de correo rechazó el envío. Verifica que los correos de los colaboradores sean válidos.",
      };

      if (backendMessage && reasons[backendMessage]) {
        return reasons[backendMessage];
      }

      // Network/timeout errors never reach the backend.
      if (err && !err.response) {
        return "No se pudo conectar con el servidor. Revisa tu conexión e inténtalo nuevamente.";
      }

      // The backend returns user-facing messages for the remaining cases
      // (e.g. the email provider's rejection reason), so show them directly.
      if (backendMessage) {
        return backendMessage;
      }

      return fallback;
    },
    async handleSendEmail(id) {
      const collaborator = this.collaborators.find((c) => c.id === id);
      if (collaborator && !String(collaborator.email || "").trim()) {
        this.$buefy.toast.open({
          message:
            "El colaborador no tiene un correo registrado. Edítalo para agregar uno antes de enviar la encuesta.",
          type: "is-danger",
        });
        return;
      }

      await sendSurveyCollaborator(id)
        .then(async () => {
          await this.getCollaborators();
          this.$buefy.toast.open({
            message: "Encuesta enviada exitosamente 🎉",
            type: "is-success",
          });
        })
        .catch((err) => {
          this.$buefy.toast.open({
            message: this.resolveEmailErrorMessage(
              err,
              "No se pudo enviar la encuesta. Por favor, inténtalo nuevamente.",
            ),
            type: "is-danger",
            duration: 6000,
          });
        });
    },
    async handleCalculated() {
      await calculatedSurveysCollaborator(this.currentProcess._id)
        .then(async (res) => {
          if (!res) {
            return this.$buefy.toast.open({
              message: "Todas las encuestas estan calculadas",
              type: "is-info",
            });
          }

          store.dispatch("selectProcess", res);

          await this.getCollaborators();
          await calculateBoardByProcess(this.currentProcess._id);

          this.$buefy.toast.open({
            message: "Encuesta calculada exitosamente 🎉",
            type: "is-success",
          });
        })
        .catch((err) => {
          console.log(err);
          this.$buefy.toast.open({
            message:
              "Ocurrió un error al calcular la encuesta. Por favor, inténtalo nuevamente.",
            type: "is-danger",
          });
        });
    },
    async handleSendAllEmails() {
      if (!this.isProcessActive) {
        this.$buefy.toast.open({
          message:
            "Solo puedes enviar correos cuando el proceso está en estado Activo",
          type: "is-warning",
        });
        return;
      }

      await sendSurveys(this.currentProcess._id)
        .then(async (res) => {
          if (!res) {
            this.$buefy.toast.open({
              message: "No hay encuestas para enviar",
              type: "is-info",
            });
            return;
          }
          await this.getCollaborators();
          // Some (or all) recipients may have been rejected even on a 200
          // response. Show exactly who failed and why in a dedicated modal.
          if (res.failures && res.failures.length) {
            this.sendFailures = {
              sent: res.sent || 0,
              failed: res.failed || 0,
              message: res.message,
              items: res.failures,
            };
            this.isFailuresModalActive = true;
            return;
          }
          this.$buefy.toast.open({
            message: "Encuestas enviadas exitosamente 🎉",
            type: "is-success",
          });
        })
        .catch((err) => {
          this.$buefy.toast.open({
            message: this.resolveEmailErrorMessage(
              err,
              "No se pudieron enviar las encuestas. Por favor, inténtalo nuevamente.",
            ),
            type: "is-danger",
            duration: 6000,
          });
        });
    },
    async handleShowEmailHistory(data) {
      await fetchHistoryEmail(data._id)
        .then(async (res) => {
          if (!res) return;

          this.emailHistoryModal = {
            isOpen: true,
            data: { id: data._id, name: data.name, ...res },
          };
        })
        .catch(() => {
          this.$buefy.toast.open({
            message:
              "Ocurrió un error al obtener el historial. Por favor, inténtalo nuevamente.",
            type: "is-danger",
          });
        });
    },
    // Download
    downloadCollaborators(scope) {
      try {
        const onlyPending = scope === "pending";
        const rows = this.buildCollaboratorRows(onlyPending);

        if (!rows.length) {
          this.$buefy.toast.open({
            message: "No hay colaboradores para descargar",
            type: "is-info",
          });
          return;
        }

        const ws = XLSX.utils.json_to_sheet(rows);
        ws["!cols"] = this.getDownloadColumnWidths(rows);

        const wb = XLSX.utils.book_new();
        const sheetName = onlyPending ? "No finalizados" : "Todos";
        XLSX.utils.book_append_sheet(wb, ws, sheetName);

        const processName =
          (this.currentProcess && this.currentProcess.name) || "Proceso";
        XLSX.writeFile(
          wb,
          `Colaboradores - ${processName} (${sheetName}).xlsx`,
        );

        this.isDownloadModalActive = false;
      } catch (err) {
        console.log(err);
        this.$buefy.toast.open({
          message: "Hubo un problema y no se pudo generar el Excel",
          type: "is-danger",
        });
      }
    },
    buildCollaboratorRows(onlyPending) {
      const isFinished = (status) =>
        ["finished", "calculated"].includes(status);
      const source = onlyPending
        ? this.collaborators.filter((c) => !isFinished(c.status))
        : this.collaborators;

      return source.map((c) => {
        const row = {
          Nombre: c.name,
          Documento: c.identification,
          Correo: c.email && c.email.length ? c.email : "-",
          Estado: this.getStatusLabel(c.status),
        };

        // Dependency columns, resolved the same way as the table.
        this.dependencies.forEach((dep) => {
          const found = (c.dependencies || []).find((d) => {
            const id =
              d.dependency && d.dependency._id
                ? d.dependency._id
                : d.dependency;
            return String(id) === String(dep._id);
          });
          row[dep.name] = found && found.value != null ? found.value : "-";
        });

        row["Fecha de ingreso"] = formatDate(c.createdAt);
        row[
          "Enlace de la encuesta"
        ] = `${window.location.origin}/cultura/survey/${c._id}`;

        return row;
      });
    },
    downloadFailures() {
      const rows = this.sendFailures.items.map((f) => ({
        Nombre: f.name && f.name.length ? f.name : "-",
        Correo: f.email && f.email.length ? f.email : "-",
        Motivo: f.reason && f.reason.length ? f.reason : "-",
      }));

      if (!rows.length) return;

      const ws = XLSX.utils.json_to_sheet(rows);
      ws["!cols"] = this.getDownloadColumnWidths(rows);

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Correos fallidos");

      const processName =
        (this.currentProcess && this.currentProcess.name) || "Proceso";
      XLSX.writeFile(wb, `Correos fallidos - ${processName}.xlsx`);
    },
    getDownloadColumnWidths(rows) {
      return Object.keys(rows[0]).map((key) => {
        const maxContent = rows.reduce(
          (max, row) =>
            Math.max(max, String(row[key] == null ? "" : row[key]).length),
          key.length,
        );
        return { width: maxContent + 2 };
      });
    },
    // Others
    formatDate(dateString) {
      return formatDate(dateString);
    },
    // TEMPORAL
    handleResults(data) {
      if (!data.calculation) return;
      this.showModalResults = true;
      this.results = data.calculation;
    },
    closeResults() {
      this.showModalResults = false;
      this.results = null;
    },
  },
  watch: {
    idtoken(nv) {
      if (nv) {
        this.getCollaborators();
        this.getDependencies();
      }
    },
    searchTerm() {
      this.onQuickFilterChanged();
    },
    isAddModalActive(nv) {
      if (!nv) this.editForm = {};
    },
  },
};
</script>

<style>
@import "~ag-grid-community/styles/ag-grid.css";
@import "~ag-grid-community/styles/ag-theme-material.css";

.ag-root-wrapper {
  border-radius: 10px !important;
}

.ag-pinned-left-header,
.ag-pinned-left-cols-container,
.ag-pinned-left-floating-bottom,
.ag-pinned-left-floating-top {
  border-right: none !important;
}

.ag-pinned-right-header,
.ag-pinned-right-cols-container,
.ag-pinned-right-floating-bottom,
.ag-pinned-right-floating-top {
  border-left: none !important;
}

/* In the production build the global Bulma/buefy stylesheet ends up injected
   after the ag-grid theme and wins, stripping the default row separators
   (they show fine in dev because of the runtime style-injection order). Pin
   the row/header borders with !important so they render in both environments. */
.ag-theme-material .ag-row {
  border-bottom: 1px solid var(--ag-row-border-color, #e2e2e2) !important;
}

.ag-theme-material .ag-header {
  border-bottom: 1px solid var(--ag-border-color, #e2e2e2) !important;
}

.button-create > span {
  display: flex;
}

.dropdown-container-temp .dropdown-menu {
  min-width: 180px;
}

.dropdown-container-temp .dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  cursor: pointer;
}

.dropdown-container-temp .dropdown-item:hover {
  background-color: #f5f5f5;
}

.form-collaborators-c .label {
  font-weight: 400 !important;
}

.icon-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  margin-right: 5px;
  height: auto !important;
  font-size: 17px !important;
}

/* Keep the Estado and Correo badges on the same baseline. Both tags are the
   same fixed height and center their content; the status icon is pulled out
   of the line-box flow (line-height: 0 + fixed square) so its webfont glyph
   metrics can't nudge the tag down relative to the text-only Correo tag. */
.badge-cell .tag {
  height: 1.5rem;
  display: inline-flex;
  align-items: center;
  line-height: 1;
}
.badge-cell .tag-icon {
  margin-top: 15px;
}

.badge-cell .tag .icon-badge {
  width: 1rem;
  height: 1rem;
  margin-right: 5px;
  line-height: 0 !important;
  font-size: 16px !important;
}

.collaborator-search .dropdown-content {
  padding: 0;
}

.collaborator-search .dropdown-item.has-link a,
.collaborator-search .dropdown-item {
  padding: 0.5rem 0.75rem;
}

.collaborator-search .suggestion-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
}

.collaborator-search .suggestion-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.collaborator-search .suggestion-tag {
  flex-shrink: 0;
}

.status-filter-dropdown .dropdown-item {
  padding: 0.5rem 2rem 0.5rem 2rem;
  position: relative;
  background-color: transparent !important;
  color: inherit !important;
}

.status-filter-dropdown .dropdown-item:hover {
  background-color: #f5f5f5 !important;
}

.status-filter-dropdown .status-all-item {
  cursor: pointer;
}

.status-filter-dropdown .dropdown-item.is-active::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background-color: #7957d5;
}

.status-filter-dropdown .dropdown-item.is-active::after {
  content: "\F012C";
  font-family: "Material Design Icons";
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #7957d5;
  font-size: 16px;
}

.status-filter-dropdown .icon-badge {
  margin-right: 5px;
  font-size: 17px;
}

.status-filter-trigger {
  min-width: 220px;
  justify-content: flex-start;
  gap: 0.4rem;
}

.status-filter-trigger .filter-icon {
  color: #7a7a7a;
}

.status-filter-trigger .filter-label {
  color: #7a7a7a;
  font-weight: 500;
  margin-right: 0.25rem;
}
</style>

<style scoped>
.content-header {
  margin-bottom: 2rem;
}

.search-section {
  margin-bottom: 1.5rem;
}

.filter-section {
  margin-bottom: 1.5rem;
}

.search-column {
  max-width: 520px;
}

.ag-grid-container {
  padding: 0;
  margin-bottom: 0 !important;
}

.row-count-footer {
  padding: 0.65rem 1rem;
  border-top: 1px solid #f0f0f0;
  font-size: 0.813rem;
  color: #7a7a7a;
  text-align: right;
}

/* Stats Section */
.stats-section {
  margin-top: 0.5rem;
}

.stat-card {
  text-align: center;
}

.stat-card .title {
  margin-bottom: 0.5rem;
}

/* Mobile Card Styles */
.collaborator-card-mobile {
  padding: 1rem;
  border-bottom: 1px solid #f5f5f5;
}

.card-header-mobile {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
}

.collaborator-info-mobile {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  flex: 1;
  min-width: 0;
}

.collaborator-details-mobile {
  flex: 1;
  min-width: 0;
}

.card-actions-mobile {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
  flex-shrink: 0;
}

.card-footer-mobile {
  text-align: right;
  padding-top: 0.5rem;
  border-top: 1px solid #f5f5f5;
}

/* Download modal */
.download-modal {
  padding: 1.75rem;
}

.download-modal .subtitle {
  margin-bottom: 1.5rem;
}

.download-options {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.download-option {
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
  text-align: left;
  padding: 1rem;
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease,
    box-shadow 0.15s ease;
}

.download-option:hover {
  border-color: #7957d5;
  background: #faf8ff;
  box-shadow: 0 2px 8px rgba(121, 87, 213, 0.12);
}

.download-option__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #f1ecfb;
  color: #7957d5;
  flex-shrink: 0;
}

.download-option__text {
  display: flex;
  flex-direction: column;
}

.download-option__title {
  font-weight: 600;
  color: #363636;
}

.download-option__desc {
  font-size: 0.813rem;
  color: #7a7a7a;
}

/* Send failures modal */
.failures-modal {
  padding: 1.75rem;
}

.failures-modal .subtitle {
  margin-bottom: 1rem;
}

.failures-summary {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.failures-list {
  max-height: 340px;
  overflow-y: auto;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
}

.failures-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #f5f5f5;
}

.failures-row:last-child {
  border-bottom: none;
}

.failures-row__info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex-shrink: 0;
  max-width: 50%;
}

.failures-row__name {
  font-weight: 600;
  color: #363636;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.failures-row__email {
  font-size: 0.813rem;
  color: #7a7a7a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.failures-row__reason {
  font-size: 0.813rem;
  color: #cc0f35;
  text-align: right;
}

.failures-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.25rem;
}

/* Notification in button */
.button-with-badge {
  position: relative;
}

.button-with-badge::after {
  content: "";
  position: absolute;
  top: -0.4em;
  right: -0.4em;
  width: 0.8em;
  height: 0.8em;
  background-color: #61d261;
  border-radius: 50%;
  border: 1px solid white;
}
</style>



// WEBPACK FOOTER //
// src/components/cultura/sections/process/Collaborators.vue